import { useState, useEffect, useCallback } from 'react';
import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';
import { LabeledCheckbox } from '../../../generic/Forms';
import PropagateModal from '../../shared/SettingsPropagateModal';
import { useAddToast } from '../../../../contexts/ToastContext';
import { useAsyncValue } from '../../../../hooks';
import { patch } from '../../../../redux/api';
import { errorMessagesListFromResponse } from '../../../../helpers/errors';
import type { FailedFetchResponse } from '../../../../helpers/errors';
import type { ProjectGithubStatusCheckSettings } from '../../../../../shared/settings/project/githubStatusCheck';

type PropagateGeneralProjectTimeoutSettings = Pick<ProjectGithubStatusCheckSettings, 'projectDefaultAutomatedIntegrationhookTimeout' | 'projectDefaultProvidedIntegrationhookTimeout' | 'projectDefaultIntegrationhookFailState'>;

type Props = {
  //  If the modal is open or not
  isOpen: boolean;
  //  We need the current state of the settings so that we can
  //  alter the text on the checkboxes so that it makes sense.
  settings: PropagateGeneralProjectTimeoutSettings;
  //  Hide the modal
  onHide: () => void;
  // Endpoint for propagating settings
  endpoint: string;
};

//  All the propagate checkboxes are disabled by default
const resetCheckboxState = {
  projectDefaultAutomatedIntegrationhookTimeout: false,
  projectDefaultProvidedIntegrationhookTimeout: false,
  projectDefaultIntegrationhookFailState: false,
};

/**
 * Hook for managing the state of the checkboxes and making the PATCH request.
 */
const usePropagateGeneralProjectTimeoutSettings = ({
  isOpen,
  onHide,
  endpoint,
}: Omit<Props, 'settings'>) => {
  const addToast = useAddToast();
  //  Void here because we don't really care about the stored value, we're just using this to track the status of the PATCH request.
  const asyncValue = useAsyncValue<void>();
  const [checkboxes, setCheckboxes] = useState<Record<keyof PropagateGeneralProjectTimeoutSettings, boolean>>(
    resetCheckboxState,
  );

  //  Reset the checkboxes when the modal closes.
  //  We always want the user to select which options should propagate,
  //  which is why none should be checked when the user opens the modal
  useEffect(() => {
    setCheckboxes(resetCheckboxState);
  }, [isOpen]);

  const onSavePropagateSettings = useCallback(async () => {
    try {
      asyncValue.setPending();
      await patch<Array<keyof PropagateGeneralProjectTimeoutSettings>>(
        endpoint,
        _.compact(
          Object.entries(checkboxes)
            .map(([key, value]) => (value ? (key as keyof PropagateGeneralProjectTimeoutSettings) : null)),
        ),
      );
      asyncValue.setResolve();
      //  Show toast and hide the modal after we successfully propagate.
      addToast({ type: 'success', content: 'Settings successfully applied to all projects.' });
      onHide();
    } catch (error) {
      asyncValue.setReject(
        errorMessagesListFromResponse(error as FailedFetchResponse),
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: check if this is correct.
  }, [addToast, asyncValue, checkboxes, onHide]);

  return {
    checkboxes,
    setCheckboxes,
    onSavePropagateSettings,
    isLoading: asyncValue.isLoading,
    errors: asyncValue.errors,
    isSaveButtonDisabled: asyncValue.isLoading || _.isEqual(checkboxes, resetCheckboxState),
  };
};

/**
 * PropagateGeneralProjectTimeoutSettingsModal is a modal for displaying the propagation
 * options and making the PATCH request to propagate the settings.
 */
const PropagateGeneralProjectTimeoutSettingsModal: VoidFunctionComponent<Props> = ({
  isOpen,
  settings,
  onHide,
  endpoint,
}) => {
  const {
    errors,
    isLoading,
    checkboxes,
    setCheckboxes,
    isSaveButtonDisabled,
    onSavePropagateSettings,
  } = usePropagateGeneralProjectTimeoutSettings({
    isOpen,
    onHide,
    endpoint,
  });

  return (
    <PropagateModal
      settingsCategory="github status check"
      isOpen={isOpen}
      errors={errors}
      onHide={onHide}
      onSave={onSavePropagateSettings}
      isSaveButtonDisabled={isSaveButtonDisabled}
      isSaveButtonLoading={isLoading}
    >
      <LabeledCheckbox
        label={`Set the default automated integration hook timeout to ${settings.projectDefaultAutomatedIntegrationhookTimeout} minutes for all projects`}
        isChecked={checkboxes.projectDefaultAutomatedIntegrationhookTimeout}
        onChange={(_event, isChecked) => setCheckboxes(currentCheckboxes => ({
          ...currentCheckboxes,
          projectDefaultAutomatedIntegrationhookTimeout: isChecked,
        }))}
        isRippleDisabled
      />
      <LabeledCheckbox
        label={`Set the default provided integration hook timeout to ${settings.projectDefaultProvidedIntegrationhookTimeout} minutes for all projects`}
        isChecked={checkboxes.projectDefaultProvidedIntegrationhookTimeout}
        onChange={(_event, isChecked) => setCheckboxes(currentCheckboxes => ({
          ...currentCheckboxes,
          projectDefaultProvidedIntegrationhookTimeout: isChecked,
        }))}
        isRippleDisabled
      />
      <LabeledCheckbox
        label={`Show a ${settings.projectDefaultIntegrationhookFailState === 'ERROR' ? 'failure' : 'success'} message after a build for all projects`}
        isChecked={checkboxes.projectDefaultIntegrationhookFailState}
        onChange={(_event, isChecked) => setCheckboxes(currentCheckboxes => ({
          ...currentCheckboxes,
          projectDefaultIntegrationhookFailState: isChecked,
        }))}
        isRippleDisabled
      />
    </PropagateModal>
  );
};

export default PropagateGeneralProjectTimeoutSettingsModal;
